import { FC } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSetRecoilState } from "recoil";

import {
  IInjectedDrawerProps,
  isSupplierPriceRatesPasswordVerifiedAtom,
  ISupplierPriceRatesPasswordDrawerProps,
  Log,
  PASSWORD_GATE_INPUT_DATA_ATTRS,
  RoutePaths,
} from "@ryde/shared";

import { CommonDefaultDrawer } from "../common-default-drawer";
import { BaseDrawer, FormFooter } from "../drawer";
import { SupplierPriceRatesPasswordWrapper } from "./parts/supplier-price-rates-password.styled";

const SUPPLIER_PRICE_RATES_PASSWORD = "111";

interface ISupplierPriceRatesPasswordForm {
  supplierPriceRatesPassword: string;
}

export const SupplierPriceRatesPasswordDrawer: FC<
  ISupplierPriceRatesPasswordDrawerProps & IInjectedDrawerProps
> = ({ onCloseDrawer, drawerNumber }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setIsPasswordVerified = useSetRecoilState(
    isSupplierPriceRatesPasswordVerifiedAtom,
  );

  const { control, handleSubmit, watch } =
    useForm<ISupplierPriceRatesPasswordForm>({
      defaultValues: {
        supplierPriceRatesPassword: "",
      },
    });

  const supplierPriceRatesPassword = watch("supplierPriceRatesPassword");

  const onSubmit = (data: ISupplierPriceRatesPasswordForm): void => {
    if (data.supplierPriceRatesPassword === SUPPLIER_PRICE_RATES_PASSWORD) {
      setIsPasswordVerified(true);
      onCloseDrawer();
      Log.infoAndToast(t("ui.PASSWORD_CONFIRMED"));
      // Use setTimeout to avoid infinite loop with useEffect in SupplierPriceRatesPage
      setTimeout(() => {
        navigate(RoutePaths.SUPPLIER_PRICE_RATES);
      }, 100);
    } else {
      Log.warnAndToast(t("ui.INCORRECT_PASSWORD"));
    }
  };

  const handleCancel = (): void => {
    onCloseDrawer();
  };

  return (
    <BaseDrawer onCloseDrawer={onCloseDrawer} drawerNumber={drawerNumber}>
      <CommonDefaultDrawer
        title={t("ui.SUPPLIERS_PRICE_RATES_PASSWORD")}
        onCloseDrawer={onCloseDrawer}
        Footer={
          <FormFooter
            cancelLabel={t("general.CANCEL")}
            onCancelClick={handleCancel}
            onSaveClick={handleSubmit(onSubmit)}
            isSaveDisabled={!supplierPriceRatesPassword}
          />
        }
      >
        <SupplierPriceRatesPasswordWrapper>
          <div className="login-section">
            <h2 className="login-title">
              {t("ui.ENTER_PASSWORD_TO_ACCESS_SUPPLIER_PRICE_RATES")}
            </h2>
            <div className="password-field-wrapper">
              <label
                htmlFor="supplier-price-rates-access-code-input"
                className="password-label"
              >
                {t("ui.PASSWORD")}*
              </label>
              <Controller
                control={control}
                name="supplierPriceRatesPassword"
                render={({ field }) => (
                  <input
                    id="supplier-price-rates-access-code-input"
                    name="supplier-price-rates-access-code"
                    type="text"
                    className="password-input password-gate-obfuscated"
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    {...PASSWORD_GATE_INPUT_DATA_ATTRS}
                    onKeyDown={e => {
                      if (e.key === "Enter" && supplierPriceRatesPassword) {
                        handleSubmit(onSubmit)();
                      }
                    }}
                    aria-label={t("ui.PASSWORD")}
                  />
                )}
              />
            </div>
          </div>
        </SupplierPriceRatesPasswordWrapper>
      </CommonDefaultDrawer>
    </BaseDrawer>
  );
};
